// src/services/api.js
import axios from "axios";

// Crear instancia de axios con configuración base
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:8000/api",
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  withCredentials: true, // Para enviar cookies si usas sesiones
});

// Interceptor para añadir el token a todas las peticiones
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      // 1. Cabecera estándar
      config.headers.Authorization = `Bearer ${token}`;
      
      // 2. Fallback para Banahosting (si no estamos en local, inyectamos el token en los parámetros URL)
      if (window.location.hostname !== "localhost" && window.location.hostname !== "127.0.0.1") {
        config.params = {
          ...config.params,
          token: token
        };
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar respuestas y errores
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Si el error es 401 (no autorizado), redirigir al login
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

export const authService = {
  // Login
  async login(credentials) {
    try {
      const response = await api.post("/login", credentials);
      if (response.data.access_token) {
        localStorage.setItem("token", response.data.access_token);
        localStorage.setItem("user", JSON.stringify(response.data.user));
      }
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Logout
  async logout() {
    try {
      await api.post("/logout");
    } catch (error) {
      console.error("Error en logout:", error);
    } finally {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("menus");
    }
  },

  // Obtener usuario actual
  async getUser() {
    try {
      const response = await api.get("/user");
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Obtener menús del usuario (estructura plana)
  async getMenus() {
    try {
      const response = await api.get("/menus");
      localStorage.setItem("menus", JSON.stringify(response.data));
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Obtener menús del usuario (estructura jerárquica)
  async getUserMenus() {
    try {
      const response = await api.get("/user/menus");
      localStorage.setItem("menus", JSON.stringify(response.data));
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Solicitar enlace de recuperación de contraseña
  async forgotPassword(email) {
    try {
      const response = await api.post("/password/forgot", { email });
      return response.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        "Error al enviar el correo de recuperación";
      throw new Error(errorMessage);
    }
  },

  // Verificar si el token de reseteo es válido
  async verifyResetToken(token, email) {
    try {
      const response = await api.post("/password/verify-token", {
        token,
        email,
      });
      return response.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Token inválido o expirado";
      throw new Error(errorMessage);
    }
  },

  // Resetear la contraseña con el token
  async resetPassword(data) {
    try {
      const response = await api.post("/password/reset", data);
      return response.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Error al restablecer la contraseña";
      throw new Error(errorMessage);
    }
  },
};

export const menuService = {
  async getAll() {
    const response = await api.get("/menus");
    return response.data;
  },
};

export const viajesService = {
  async getAll(params = {}) {
    const response = await api.get("/viajes", { params });
    return response.data;
  },

  async getById(id) {
    const response = await api.get(`/viajes/${id}`);
    return response.data;
  },

  async create(data) {
    const response = await api.post("/viajes", data);
    return response.data;
  },

  async update(id, data) {
    const response = await api.put(`/viajes/${id}`, data);
    return response.data;
  },

  async delete(id) {
    const response = await api.delete(`/viajes/${id}`);
    return response.data;
  },

  async getNotificacionesConfig(id) {
    const response = await api.get(`/viajes/${id}/notificaciones-config`);
    return response.data;
  },

  async updateNotificacionesConfig(id, notificaciones) {
    const response = await api.put(`/viajes/${id}/notificaciones-config`, { notificaciones });
    return response.data;
  },
};

export const geocercasService = {
  async getAll(params = {}) {
    const response = await api.get("/geocercas", { params });
    return response.data;
  },

  async getById(id) {
    const response = await api.get(`/geocercas/${id}`);
    return response.data;
  },

  async create(data) {
    const response = await api.post("/geocercas", data);
    return response.data;
  },

  async update(id, data) {
    const response = await api.put(`/geocercas/${id}`, data);
    return response.data;
  },

  async delete(id) {
    const response = await api.delete(`/geocercas/${id}`);
    return response.data;
  },
};

export const reglasNotificacionService = {
  async getAll(params = {}) {
    const response = await api.get("/reglas-notificacion", { params });
    return response.data;
  },

  async create(data) {
    const response = await api.post("/reglas-notificacion", data);
    return response.data;
  },

  async update(id, data) {
    const response = await api.put(`/reglas-notificacion/${id}`, data);
    return response.data;
  },

  async delete(id) {
    const response = await api.delete(`/reglas-notificacion/${id}`);
    return response.data;
  },

  async toggleActivo(id) {
    const response = await api.patch(`/reglas-notificacion/${id}/toggle-activo`);
    return response.data;
  },

  async tiposEventos() {
    const response = await api.get("/reglas-notificacion/tipos-eventos");
    return response.data.data ?? response.data;
  },

  async variablesPlantilla() {
    const response = await api.get("/reglas-notificacion/variables-plantilla");
    return response.data.data ?? response.data;
  },

  async previsualizarMensaje(data) {
    const response = await api.post("/reglas-notificacion/previsualizar-mensaje", data);
    return response.data.data ?? response.data;
  },

  async probar(id) {
    const response = await api.post(`/reglas-notificacion/${id}/probar`);
    return response.data;
  },
};

export const empresaNotificacionesService = {
  async getConfig(empresaId) {
    const response = await api.get(`/empresas/${empresaId}/notificaciones-config`);
    return response.data;
  },
  async updateConfig(empresaId, notificaciones, velocidadLimite, contactosSms) {
    const payload = { notificaciones };
    if (velocidadLimite !== undefined) payload.velocidad_limite = velocidadLimite;
    if (contactosSms !== undefined) payload.contactos_sms = contactosSms;
    const response = await api.put(`/empresas/${empresaId}/notificaciones-config`, payload);
    return response.data;
  },
  async updateContactosSms(empresaId, contactosSms) {
    const response = await api.put(`/empresas/${empresaId}/notificaciones-config`, {
      contactos_sms: contactosSms,
    });
    return response.data;
  },
};

export const notificacionesService = {
  async getAll(params = {}) {
    const response = await api.get("/notificaciones", { params });
    return response.data;
  },

  async estadisticas(params = {}) {
    const response = await api.get("/notificaciones/estadisticas", { params });
    return response.data;
  },

  async reenviar(id) {
    const response = await api.post(`/notificaciones/${id}/reenviar`);
    return response.data;
  },
};

export default api;
